import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { ProAutomationService } from './pro-automation.service';

/**
 * Pro-mode "Automation" setup — one screen for a Pro admin to (1) set the company
 * persona the bot speaks in, (2) choose which lead sources feed the Default journey,
 * and (3) attach workflows (each with an example message) to the Default phase. The
 * engine then routes an incoming chat to the workflow whose example best matches.
 */
@Component({
  selector: 'app-pro-automation',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, InputTextModule, ToastModule, TooltipModule],
  providers: [MessageService],
  template: `
<p-toast />
<div class="pa">
  <div class="pa__head">
    <div>
      <h3>Automation</h3>
      <p class="pa__hint">Set the bot's voice, pick the lead sources it answers, and add the workflows it can run. When a customer messages, the bot picks the workflow whose <b>example</b> best matches.</p>
    </div>
    <p-button [label]="saving ? 'Saving…' : 'Save'" icon="pi pi-check" [disabled]="saving || loading" (onClick)="save()" />
  </div>

  <div *ngIf="loading" class="pa-muted pad">Loading…</div>

  <ng-container *ngIf="!loading">
    <!-- 1. Persona -->
    <section class="pa-card">
      <div class="pa-card__t"><i class="pi pi-comment"></i> Company persona</div>
      <p class="pa-muted sm">How the assistant introduces itself and talks. Example: "You are Riya, a friendly solar advisor for SunPower. Be warm, concise, and helpful."</p>
      <textarea class="pa-ta" rows="4" [(ngModel)]="persona" placeholder="Describe who the assistant is and how it should talk…"></textarea>
    </section>

    <!-- 2. Lead sources -->
    <section class="pa-card">
      <div class="pa-card__t"><i class="pi pi-link"></i> Lead sources</div>
      <p class="pa-muted sm">Tick the sources whose leads should enter this bot. Add new sources under <b>Lead Sources</b>.</p>
      <div *ngIf="!sources.length" class="pa-muted pad">No lead sources yet.</div>
      <label class="pa-row" *ngFor="let s of sources">
        <input type="checkbox" [(ngModel)]="s.attached" />
        <span class="pa-row__name"><b>{{ s.source }}</b> <span class="pa-muted sm" *ngIf="s.sourceId">· {{ s.sourceId }}</span></span>
      </label>
    </section>

    <!-- 3. Workflows on the Default phase -->
    <section class="pa-card">
      <div class="pa-card__t"><i class="pi pi-sitemap"></i> Workflows the bot can run</div>
      <p class="pa-muted sm">Turn on a workflow and give it an <b>example message</b> a customer might send. The bot uses the example to decide which workflow to run.</p>
      <div *ngIf="!workflows.length" class="pa-muted pad">No workflows yet — build one under <b>Workflows</b> first.</div>
      <div class="pa-wf" *ngFor="let w of workflows" [class.pa-wf--on]="w.attached">
        <label class="pa-wf__top">
          <input type="checkbox" [(ngModel)]="w.attached" />
          <span class="pa-wf__name"><i class="pi pi-bolt"></i> <b>{{ w.flowName }}</b></span>
          <span class="pa-badge" [attr.data-s]="w.status">{{ w.status }}</span>
        </label>
        <div class="pa-wf__ex" *ngIf="w.attached">
          <label class="pa-l">Example customer message</label>
          <input pInputText [(ngModel)]="w.example" placeholder='e.g. "I want a quote" or "Book a site visit"' />
        </div>
      </div>
    </section>

    <div class="pa-foot">
      <p-button [label]="saving ? 'Saving…' : 'Save'" icon="pi pi-check" [disabled]="saving" (onClick)="save()" />
    </div>
  </ng-container>
</div>
  `,
  styles: [`
    .pa { display:flex; flex-direction:column; gap:14px; }
    .pa__head { display:flex; align-items:flex-start; justify-content:space-between; gap:1rem; }
    .pa__head h3 { margin:0; font-size:var(--gh-fs-19); }
    .pa__hint { margin:4px 0 0; color:var(--gh-text-3); font-size:var(--gh-fs-13); max-width:52rem; }
    .pa-muted { color:var(--gh-text-muted); } .sm { font-size:var(--gh-fs-13); } .pad { padding:10px 2px; }
    .pa-card { border:1px solid var(--gh-border); border-radius:var(--gh-r-lg); padding:14px 16px; display:flex; flex-direction:column; gap:8px; background:transparent; }
    .pa-card__t { font-weight:700; display:flex; align-items:center; gap:8px; }
    .pa-card__t i { color: var(--primary-color); }
    .pa-ta { width:100%; padding:10px 12px; border:1px solid var(--gh-border); border-radius:var(--gh-r-md); font:inherit; resize:vertical; }
    .pa-ta:focus { outline:none; border-color:var(--gh-primary); box-shadow:0 0 0 3px rgba(22,163,74,.12); }
    .pa-row { display:flex; align-items:center; gap:10px; padding:8px 4px; border-top:1px solid var(--gh-border); cursor:pointer; }
    .pa-row:first-of-type { border-top:none; }
    .pa-row__name { min-width:0; }
    .pa-wf { border:1px solid var(--gh-border); border-radius:var(--gh-r-md); padding:10px 12px; margin-top:8px; }
    .pa-wf:first-of-type { margin-top:4px; }
    .pa-wf--on { border-color:var(--gh-primary-300); background:var(--gh-primary-wash); }
    .pa-wf__top { display:flex; align-items:center; gap:10px; cursor:pointer; }
    .pa-wf__name { flex:1; min-width:0; display:flex; align-items:center; gap:6px; }
    .pa-wf__name i { color: var(--primary-color); }
    .pa-badge { font-size:var(--gh-fs-11); font-weight:700; text-transform:uppercase; letter-spacing:.04em; padding:.1rem .45rem; border-radius:var(--gh-r-pill); background:var(--gh-surface-hover); color:var(--gh-text-2); }
    .pa-badge[data-s='ACTIVE'] { background:var(--gh-primary-100); color:var(--gh-primary); }
    .pa-wf__ex { margin-top:8px; padding-left:26px; }
    .pa-l { display:block; font-size:var(--gh-fs-12); font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:var(--gh-text-3); margin-bottom:4px; }
    .pa-wf__ex input[pInputText] {
      width: 100%;
      padding: 0 12px;
      border: 1px solid var(--gh-border);
      border-radius: var(--gh-r-sm);
      font: inherit;
    }
    .pa-foot { display:flex; justify-content:flex-end; }
  `],
})
export class ProAutomationComponent implements OnInit {
  persona = '';
  sources: { id: number; source: string; sourceId: string; attached: boolean }[] = [];
  workflows: { id: string; flowName: string; status: string; attached: boolean; example: string }[] = [];
  loading = false;
  saving = false;

  constructor(private svc: ProAutomationService, private toast: MessageService) {}

  ngOnInit(): void { this.load(); }

  load(): void {
    this.loading = true;
    this.svc.get().subscribe({
      next: (res: any) => { this.apply(res?.data ?? res ?? {}); this.loading = false; },
      error: () => { this.loading = false; this.toast.add({ severity: 'error', summary: 'Could not load automation' }); },
    });
  }

  private apply(d: any): void {
    this.persona = d.persona ?? '';
    this.sources = (d.sources ?? []).map((s: any) => ({
      id: s.id, source: s.source ?? '', sourceId: s.sourceId ?? '', attached: !!s.attached,
    }));
    this.workflows = (d.workflows ?? []).map((w: any) => ({
      id: w.id, flowName: w.flowName ?? '', status: w.status ?? '', attached: !!w.attached, example: w.example ?? '',
    }));
  }

  save(): void {
    // Every attached workflow needs an example, else the engine has nothing to route on.
    const missing = this.workflows.filter(w => w.attached && !(w.example || '').trim());
    if (missing.length) {
      this.toast.add({ severity: 'warn', summary: 'Add an example', detail: `Give an example message for: ${missing.map(w => w.flowName).join(', ')}` });
      return;
    }
    const body = {
      persona: this.persona ?? '',
      sourceIds: this.sources.filter(s => s.attached).map(s => s.id),
      workflows: this.workflows.filter(w => w.attached).map(w => ({
        workflowId: w.id, workflowType: 'workflow', example: (w.example || '').trim(),
      })),
    };
    this.saving = true;
    this.svc.save(body).subscribe({
      next: (res: any) => {
        this.saving = false;
        const d = res?.data ?? res ?? {};
        this.apply(d);
        if (d.failed?.length) {
          this.toast.add({ severity: 'warn', summary: 'Some workflows could not activate', detail: (d.failed as string[]).join(', ') });
        } else {
          this.toast.add({ severity: 'success', summary: 'Automation saved' });
        }
      },
      error: (e: any) => { this.saving = false; this.toast.add({ severity: 'error', summary: 'Save failed', detail: e?.error?.message || e?.message }); },
    });
  }
}
